// src/modules/home/components/TopBar.jsx
// LiveTicker (stat strip) + FilterBar + shared micro-components

import { useState, useEffect } from 'react';
import { LIVE_STATS, TRACK_META, POST_TYPES } from '../data/homeData';
import { FILTERS } from '../hooks/useFeed';

// ─── shared design tokens ──────────────────────────────────────────────────────
export const T = {
  void:    'var(--void)',
  deep:    'var(--deep)',
  surface: 'var(--surface)',
  b0:      'var(--b0)',
  b1:      'var(--b1)',
  b2:      'var(--b2)',
  t1:      'var(--t1)',
  t2:      'var(--t2)',
  t3:      'var(--t3)',
  t4:      'var(--t2)',
  cyan:    'var(--cyan)',
  violet:  'var(--violet)',
  green:   'var(--green)',
  gold:    'var(--gold)',
  red:     'var(--red)',
  orange:  'var(--orange)',
  mono:    "'JetBrains Mono', monospace",
  display: "'Syne', sans-serif",
};

// ─── Pill / badge ──────────────────────────────────────────────────────────────
export function Pill({ label, color, small }) {
  return (
    <span style={{
      fontSize: small ? 7 : 8,
      padding:  small ? '1px 5px' : '2px 7px',
      borderRadius: 2,
      background: `${color}18`,
      border: `1px solid ${color}33`,
      color,
      letterSpacing: '.1em',
      fontWeight: 700,
      whiteSpace: 'nowrap',
      flexShrink: 0,
    }}>{label}</span>
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────────────
export function Avatar({ entity, size = 36 }) {
  if (!entity) return null;
  const isAI = entity.type !== 'human';
  return (
    <div style={{
      width: size, height: size,
      borderRadius: isAI ? 4 : '50%',
      background: `${entity.color}20`,
      border: `1px solid ${entity.color}50`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: isAI ? size * 0.42 : size * 0.38,
      color: entity.color, fontWeight: 800, flexShrink: 0,
      position: 'relative',
      fontFamily: T.mono,
    }}>
      {isAI ? entity.avatar[0] : entity.avatar}
      {entity.online && (
        <div style={{
          position: 'absolute', bottom: -1, right: -1,
          width: size * 0.24, height: size * 0.24,
          borderRadius: '50%', background: T.green,
          border: `${size > 28 ? 2 : 1.5}px solid ${T.void}`,
        }}/>
      )}
    </div>
  );
}

// ─── LIVE TICKER ──────────────────────────────────────────────────────────────
export function LiveTicker() {
  const [tick, setTick] = useState(0);
  const [stats, setStats] = useState(LIVE_STATS);

  // Simulate live updates
  useEffect(() => {
    const id = setInterval(() => {
      setTick(t => t + 1);
      setStats(prev => prev.map(s => ({
        ...s,
        value: s.id === 'experiments'
          ? String(parseInt(s.value.replace(/,/g,'')) + Math.floor(Math.random() * 3 - 1)).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          : s.value,
      })));
    }, 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{
      display: 'flex', alignItems: 'stretch',
      borderBottom: `1px solid ${T.b1}`,
      background: T.void,
      overflow: 'hidden',
      flexShrink: 0,
    }}>
      {/* Brand mark */}
      

      {/* Stats */}
      {stats.map((s, i) => (
        <div key={s.id} style={{
          flex: 1, padding: '8px 14px',
          borderRight: `1px solid ${T.b0}`,
          display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1,
          minWidth: 0,
        }}>
          <div style={{ fontSize: 8, color: T.t3, letterSpacing: '.12em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {s.label}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
            <span style={{ fontFamily: T.display, fontWeight: 800, fontSize: 16, color: s.color }}>
              {s.value}
            </span>
            {s.delta && (
              <span style={{ fontSize: 9, color: T.green }}>↑{s.delta}</span>
            )}
          </div>
        </div>
      ))}

      {/* Live dot */}
      <div style={{
        padding: '0 14px', display: 'flex', alignItems: 'center', gap: 5,
        borderLeft: `1px solid ${T.b1}`, flexShrink: 0,
      }}>
        <div style={{
          width: 6, height: 6, borderRadius: '50%', background: T.green,
          animation: 'hpPulse 1.6s ease-in-out infinite',
        }}/>
        <span style={{ fontSize: 8, color: T.green, letterSpacing: '.14em' }}>LIVE</span>
      </div>
    </div>
  );
}

// ─── FILTER BAR ───────────────────────────────────────────────────────────────
export function FilterBar({ active, setActive }) {
  return (
    <div style={{
      display: 'flex', gap: 4, marginBottom: 10,
      overflowX: 'auto', paddingBottom: 2, flexShrink: 0,
    }}>
      {FILTERS.map(f => {
        const on = active === f.id;
        return (
          <button key={f.id} onClick={() => setActive(f.id)} style={{
            padding: '5px 12px',
            border: `1px solid ${on ? T.cyan : T.b1}`,
            background: on ? `${T.cyan}15` : 'transparent',
            color: on ? T.cyan : T.t3,
            fontSize: 9, cursor: 'pointer', borderRadius: 2,
            fontFamily: T.mono, letterSpacing: '.1em', whiteSpace: 'nowrap',
            transition: 'all .15s', flexShrink: 0,
          }}>
            {f.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── SECTION HEADER (right panel) ─────────────────────────────────────────────
export function SectionHead({ label, action, onAction }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      marginBottom: 10,
    }}>
      <span style={{ fontSize: 8, color: T.t3, letterSpacing: '.16em' }}>{label}</span>
      {action && (
        <button onClick={onAction} style={{
          background: 'none', border: 'none', color: T.cyan,
          fontSize: 9, cursor: 'pointer', fontFamily: T.mono,
        }}>{action}</button>
      )}
    </div>
  );
}
